import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { FieldFactory } from '../../../components/Form/FieldFactory';
import { FormField, FormLabelText } from '../../../components/Form/FormComponents';
import { KpiPeriod } from '../../../data-models/company-financials.data-model';
import { RendererType } from '../../../data-models/field-definition';
import { createFormField } from '../../../view-models/form.view-model';
import { controlledFormProps } from './mprFormProps';
import {
  expandMprPeriodRange,
  getDateStrForRelativePeriod,
  getFormattedConfigPeriod,
  getMprPeriodKey,
  IMprPeriodBound,
  MaxMprRangeLength,
  MonthValues,
  MprPeriod,
  mprRangeLength,
  YearValues,
} from './multiPeriodReportingUtils';

export const AddRangeLabel = 'Add a range…';

function boundFromDate(date: string): IMprPeriodBound {
  const [year, month] = date.split('-').map(Number);
  return { year, month };
}
/** The last four full periods, the range most reports start from. */
function defaultRange(period: KpiPeriod) {
  return {
    start: boundFromDate(getDateStrForRelativePeriod(period, 3)),
    end: boundFromDate(getDateStrForRelativePeriod(period, 0)),
  };
}

interface IMprPeriodRangeFormProps {
  /** The picker's length; the range is made of periods of it. */
  length: KpiPeriod;
  existing: MprPeriod[];
  /** Called with the whole list: the existing periods, then the range's new ones. */
  onAdd: (periods: MprPeriod[]) => void;
}

/** A from–to pair that checks every fixed period between them: a run too long to tick one by one. */
export function MprPeriodRangeForm({ length, existing, onAdd }: IMprPeriodRangeFormProps) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(() => defaultRange(length));
  // the bounds follow the picker's length: a range of quarters becomes one of months with it
  const [rangeLength, setRangeLength] = useState(length);
  if (rangeLength !== length) {
    setRangeLength(length);
    setRange(defaultRange(length));
  }

  const fresh = useMemo(() => {
    const present = new Set(existing.map(getMprPeriodKey));
    return expandMprPeriodRange(length, range.start, range.end).filter(
      (period) => !present.has(getMprPeriodKey(period))
    );
  }, [existing, length, range]);
  const span = mprRangeLength(length, range.start, range.end);
  const tooLong = span > MaxMprRangeLength;
  const summary = tooLong
    ? `A range can hold at most ${MaxMprRangeLength} periods`
    : span === 0
      ? 'The end is before the start'
      : fresh.length === 0
        ? 'Already in the list'
        : fresh.length > 6
          ? `${fresh.length} periods, ${getFormattedConfigPeriod(fresh[0])} to ${getFormattedConfigPeriod(fresh[fresh.length - 1])}`
          : fresh.map(getFormattedConfigPeriod).join(', ');

  if (!open) {
    return (
      <Link
        component='button'
        type='button'
        variant='body2'
        underline='hover'
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => setOpen(true)}
      >
        {AddRangeLabel}
      </Link>
    );
  }

  const subLabel = length === KpiPeriod.quarter ? 'quarter' : 'month';
  const subValues = length === KpiPeriod.year ? [] : MonthValues[length];
  const singleSelect = (key: string, label: string, values: { value: number; displayName: string }[]) =>
    createFormField({
      key,
      label,
      renderer: RendererType.singleSelect,
      rendererMeta: { values },
      disableClearable: true,
    });
  const boundField = (which: 'start' | 'end', label: string) => (
    <FormField>
      <FormLabelText label={label} />
      <Stack direction='row' gap='0.5rem'>
        {length !== KpiPeriod.year && (
          <Box flex={1}>
            <FieldFactory
              formField={singleSelect(`${which}Month`, `${label} ${subLabel}`, subValues)}
              formProps={controlledFormProps(range[which].month, (month: number) =>
                setRange((prev) => ({ ...prev, [which]: { ...prev[which], month } }))
              )}
            />
          </Box>
        )}
        <Box flex={1}>
          <FieldFactory
            formField={singleSelect(`${which}Year`, `${label} year`, YearValues)}
            formProps={controlledFormProps(range[which].year, (year: number) =>
              setRange((prev) => ({ ...prev, [which]: { ...prev[which], year } }))
            )}
          />
        </Box>
      </Stack>
    </FormField>
  );

  return (
    <Stack gap='0.5rem' role='group' aria-label='Add a range'>
      <Box display='grid' gridTemplateColumns='1fr 1fr' gap='0.5rem'>
        {boundField('start', 'From')}
        {boundField('end', 'To')}
      </Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' gap='1rem'>
        <Typography variant='caption' color='text.secondary' noWrap title={summary} minWidth={0}>
          {summary}
        </Typography>
        <Stack direction='row' gap='0.5rem' flexShrink={0}>
          <Button variant='text' color='secondary' size='medium' onClick={() => setOpen(false)}>
            Close
          </Button>
          <Button
            variant='contained'
            color='secondary'
            size='medium'
            disabled={fresh.length === 0 || tooLong}
            onClick={() => {
              onAdd([...existing, ...fresh]);
              setOpen(false);
            }}
          >
            {fresh.length ? `Add ${fresh.length} period${fresh.length === 1 ? '' : 's'}` : 'Add periods'}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
